import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import {
  WeatherExVariableMeta,
  WeatherExPointRow,
} from '../weatherex-point-drawer/weatherex-point-drawer.component';

/**
 * Compact floating tooltip for a single WeatherEx point (AOI / WMS path).
 *
 * Appears near the map marker on hover. Shows the currently active variable
 * value prominently at the top, with all other variables in a collapsible
 * "All Variables" section. Values are formatted to 2 decimal places.
 *
 * Positioned by the parent via [pixelPosition] (pixel coords relative to the
 * Leaflet map container). Visibility is controlled by [visible].
 */
@Component({
  selector: 'app-weatherex-point-tooltip',
  templateUrl: './weatherex-point-tooltip.component.html',
  styleUrls: ['./weatherex-point-tooltip.component.scss'],
})
export class WeatherexPointTooltipComponent implements OnChanges {
  /** Active GeoJSON feature (properties contain per-variable scalars + lat/lon). */
  @Input() feature: any = null;
  /** Ordered variable names to display. */
  @Input() variables: string[] = [];
  /** Variable → unit string (runtime, from the GeoJSON/S3 data). */
  @Input() units: { [key: string]: string } = {};
  /** Static variable metadata from ApiService.weatherVariables. */
  @Input() variableMeta: WeatherExVariableMeta[] = [];
  /** Unified ISO time axis (UTC). */
  @Input() times: string[] = [];
  /** Index into `times` for the currently selected time step. */
  @Input() selectedTimeIndex = 0;
  /** True while GetFeatureInfo is in-flight. */
  @Input() loading = false;
  /** Variable names whose WMS layer is currently checked — the first match is shown on top. */
  @Input() activeVariables: string[] = [];
  /** When true the tooltip renders all variables in a single flat list (no Active/Other split). */
  @Input() flatMode = false;
  /** Pixel position {x, y} relative to the Leaflet map container. Set by parent on marker hover. */
  @Input() pixelPosition: { x: number; y: number } | null = null;
  /** Whether the tooltip is shown. */
  @Input() visible = false;
  /**
   * Which corner of the marker to open towards.
   * 'above-right' | 'below-right' | 'above-left' | 'below-left'
   * Computed by the parent from map.getSize() + pixel position.
   */
  @Input() placement = 'above-right';
  /** Maximum height in px the popup card may grow to (computed from map geometry by the controller). 0 = unconstrained. */
  @Input() maxHeight = 0;

  /** Reflects placement onto a data-attribute so SCSS can select it. */
  @HostBinding('attr.data-placement') get dataPlacement(): string {
    return this.placement;
  }

  /** Emits when the user clicks the close button. */
  @Output() closed = new EventEmitter<void>();
  /** Emits when the mouse leaves both the marker and the tooltip. */
  @Output() tooltipMouseleave = new EventEmitter<void>();

  /** All rows used in flatMode. */
  allRows: WeatherExPointRow[] = [];
  /** All rows for variables whose WMS layer is currently active/checked. */
  activeRows: WeatherExPointRow[] = [];
  /** All rows for variables NOT in any active layer. */
  otherRows: WeatherExPointRow[] = [];
  /** Whether the "Other Variables" section is expanded. */
  expanded = false;

  /** Suppresses a spurious mouseleave that fires when the popup resizes after toggling. */
  private _suppressLeave = false;
  private _suppressLeaveTimer: any = null;

  ngOnChanges(): void {
    const rows = this.buildRows();
    if (this.flatMode) {
      this.allRows    = rows;
      this.activeRows = [];
      this.otherRows  = [];
    } else {
      this.allRows    = [];
      this.activeRows = rows.filter(r => r.isActive);
      this.otherRows  = rows.filter(r => !r.isActive);
    }
  }

  get latitude(): number | null {
    const lat = this.feature?.properties?.latitude;
    return Number.isFinite(lat) ? Number(lat) : null;
  }

  get longitude(): number | null {
    const lon = this.feature?.properties?.longitude;
    return Number.isFinite(lon) ? Number(lon) : null;
  }

  get timeIso(): string {
    return this.times[this.selectedTimeIndex] ?? '';
  }

  /** Inline style for the host: positions the tooltip near the marker. */
  get tooltipStyle(): { [key: string]: string } {
    if (!this.pixelPosition) return { display: 'none' };
    const style: { [key: string]: string } = {
      left: `${this.pixelPosition.x}px`,
      top:  `${this.pixelPosition.y}px`,
    };
    if (this.maxHeight > 0) {
      style['max-height'] = `${this.maxHeight}px`;
    }
    return style;
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
    // Suppress the mouseleave that fires when the popup resizes (cursor
    // ends up outside the shrunken/grown tooltip bounds for one frame).
    this._suppressLeave = true;
    clearTimeout(this._suppressLeaveTimer);
    this._suppressLeaveTimer = setTimeout(() => {
      this._suppressLeave = false;
      this._suppressLeaveTimer = null;
    }, 150);
  }

  onClose(): void {
    this.expanded = false;
    this.closed.emit();
  }

  onMouseLeave(): void {
    if (this._suppressLeave) return;
    this.tooltipMouseleave.emit();
  }

  trackByKey(_index: number, row: WeatherExPointRow): string {
    return row.key;
  }

  private buildRows(): WeatherExPointRow[] {
    const props = this.feature?.properties ?? {};
    const lookup = new Map<string, WeatherExVariableMeta>();
    for (const entry of this.variableMeta) lookup.set(entry.key, entry);

    return this.variables.map((variable) => {
      const meta = lookup.get(variable);
      const unit = meta?.unit ?? this.units[variable] ?? '';
      const series = props[variable];
      const raw = Array.isArray(series)
        ? series[this.selectedTimeIndex]
        : series !== null && series !== undefined
        ? series
        : undefined;

      let display: string;
      if (raw === null || raw === undefined || Number.isNaN(Number(raw))) {
        display = 'N/A';
      } else {
        const formatted =
          typeof raw === 'number' ? raw.toFixed(2) : Number(raw).toFixed(2);
        display = `${formatted}${unit ? '\u00a0' + unit : ''}`;
      }

      return {
        key: variable,
        label: meta?.name ?? variable.replace(/_/g, ' '),
        value: display,
        unit,
        description: meta?.description ?? '',
        isActive: this.activeVariables.includes(variable),
      };
    });
  }
}
